/**
 * Markers on an NxSlider: clickable marks on a track above the slider (events
 * on a replay timeline, chapters on a scrubber). Pure helpers, shared by the
 * component and its tests.
 */

/** The mark's form: a dot, a diamond, or a dot split into both colours. */
export type NxSliderMarkerShape = 'dot' | 'diamond' | 'split';

/**
 * A state on top of the shape's colour: `muted` greys the mark out, `warning`
 * rings it (the shape's colour stays readable), `danger` turns it red and rings
 * it. `default` is the shape's own colour.
 */
export type NxSliderMarkerTone = 'default' | 'muted' | 'warning' | 'danger';

export interface NxSliderMarker {
  /** Stable identity; emitted back with `marker-select`. */
  id: string;
  /** Place along the track, 0 (start) … 1 (end). Clamped. */
  position: number;
  /** Accessible name and tooltip — say what the mark stands for. */
  label: string;
  /** Row inside the marker track, 0 = top. Keeps neighbouring marks apart. */
  lane?: number;
  shape?: NxSliderMarkerShape;
  tone?: NxSliderMarkerTone;
  /** How many entries the mark bundles; above 1 a count badge shows. */
  count?: number;
}

/** Highest lane a marker may use; more rows would no longer be compact. */
export const SLIDER_MARKER_MAX_LANE = 3;

export function sliderMarkerPosition(position: number): number {
  if (!Number.isFinite(position)) return 0;
  return Math.min(1, Math.max(0, position));
}

export function sliderMarkerLane(lane: number | undefined): number {
  if (lane === undefined || !Number.isFinite(lane)) return 0;
  return Math.min(SLIDER_MARKER_MAX_LANE, Math.max(0, Math.floor(lane)));
}

/** Number of lanes the track needs (at least one). */
export function sliderMarkerLaneCount(markers: readonly NxSliderMarker[]): number {
  return markers.reduce((count, marker) => Math.max(count, sliderMarkerLane(marker.lane) + 1), 1);
}

/** Markers in reading order along the track: by position, then lane. */
export function orderSliderMarkers(markers: readonly NxSliderMarker[]): NxSliderMarker[] {
  return [...markers].sort(
    (a, b) =>
      sliderMarkerPosition(a.position) - sliderMarkerPosition(b.position) ||
      sliderMarkerLane(a.lane) - sliderMarkerLane(b.lane)
  );
}

/** Badge text for a bundled mark; empty when it stands for one entry. */
export function sliderMarkerCountText(count: number | undefined): string {
  if (count === undefined || !Number.isFinite(count) || count <= 1) return '';
  return count > 99 ? '99+' : String(Math.floor(count));
}

/** Index of the ordered marker closest to a 0…1 fraction (the tab stop). */
export function nearestSliderMarker(ordered: readonly NxSliderMarker[], fraction: number): number {
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  ordered.forEach((marker, index) => {
    const distance = Math.abs(sliderMarkerPosition(marker.position) - fraction);
    if (distance < bestDistance) {
      best = index;
      bestDistance = distance;
    }
  });
  return best;
}
